<script setup lang="ts" generic="T extends Record<string, any>">
import { onMounted, ref, computed, watch } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { useRoute, useRouter } from "vue-router";
import type { DataTableFilterMeta } from "primevue/datatable";
import SharedEntityDrawer from "src/components/shared/SharedEntityDrawer.vue";
import { SchemaType } from "src/enums/schema-types";
import type { DataTableFilterType } from "src/types/data-table";
import type { EntitySchema } from "src/types/entity-schema";
import { useConfirm } from "primevue/useconfirm";
import pluralize from "pluralize";
import TieredMenu from "primevue/tieredmenu";
import Button from "primevue/button";
import type { CrudTableColumn, CrudTableColumnOptions, TableColumns, TableColumnsDefault } from "src/router/types";
import Column from "primevue/column";
import SharedEntityNestedDataTable from "./SharedEntityNestedDataTable.vue";
import SharedJsonDisplay from './SharedJsonDisplay.vue';
import { shouldDisplayAsJson } from 'src/utils/json-utils';

const { schema, filters, tableColumns, tableColumnsDefault } = defineProps<{
  schema: EntitySchema;
  filters: DataTableFilterMeta;
  tableColumns: TableColumns;
  tableColumnsDefault?: TableColumnsDefault[];
}>();

const confirm = useConfirm();
const route = useRoute();
const router = useRouter();

const uuidParam = computed(() => route.query.uuid as string | undefined);

const { data: fetchAllData, isPending: fetchAllPending, mutateAsync } = schema.requests.fetchAll();

const fetchOneQuery = uuidParam.value && typeof schema.requests.fetchOne === "function"
  ? (schema.requests.fetchOne as (id: string) => { data: any; isLoading: any })(uuidParam.value)
  : null;

const data = computed(() => {
  if (uuidParam.value && fetchOneQuery?.data?.value) {
    return [fetchOneQuery.data.value];
  }
  return fetchAllData.value?.data ?? fetchAllData.value;
});

const isPending = computed(() => {
  if (uuidParam.value) return fetchOneQuery?.isLoading?.value ?? false;
  return fetchAllPending.value;
});

const isDrawerVisible = ref(false);
const type = ref<SchemaType | null>(null);
const selectedItem = ref<T | null>(null);

const actions = {
  create: !!schema.crud?.create,
  delete: !!schema.crud?.delete,
  edit: !!schema.crud?.update,
  view: typeof schema.requests.fetchOne === "function",
  details: !!schema.crud?.details,
};

const filterType = defineModel<DataTableFilterType>("filterType", {
  default: "simple",
});

const selectedRelationEntityName = ref<string | null>(null);
const relationMenuRefs = ref<Record<string, any>>({});

const relationMenuItems = ref([
  {
    label: "Edit",
    icon: "pi pi-fw pi-pencil",
    command: () => {
      if (selectedRelationEntityName.value && selectedItem.value) {
        onEditRelation(selectedRelationEntityName.value, selectedItem.value);
      }
    },
  },
  {
    label: "View",
    icon: "pi pi-fw pi-eye",
    command: () => {
      if (selectedRelationEntityName.value && selectedItem.value) {
        navigateToRelation(selectedRelationEntityName.value, selectedItem.value);
      }
    },
  },
]);

const crudTableColumns = computed(() => {
  const columns = {
    ...getFilteredTableColumns(),
  };

  // Remove "actions" if present — the actions column with edit/delete/details
  // buttons is rendered separately by SharedDataTable
  delete columns["actions"];

  return columns;
});

const isArrayDialogVisible = ref(false);
const arrayDialogData = ref<any[]>([]);
const arrayColumns = ref<string[]>([]);
const arrayDialogEntityName = ref<string | null>(null);
const arrayDialogOptions = ref<CrudTableColumnOptions | undefined>(undefined);
const arrayDialogParentId = ref<string>('');
const emit = defineEmits<{
  "delete:array-item": [item: T];
}>();

function getFilteredTableColumns() {
  if (!tableColumnsDefault) {
    return tableColumns;
  }

  const defaultColumnNames = tableColumnsDefault?.map((col) => col.name);
  return Object.fromEntries(Object.entries(tableColumns).filter(([key]) => defaultColumnNames.includes(key)));
}

function onClickCreate() {
  if (!schema.requests.create) {
    router.push({
      name: schema.crud.create as string,
    });
    return;
  }

  selectedItem.value = data;
  type.value = SchemaType.CREATE;
  isDrawerVisible.value = true;
}

function onClickEdit(item: T) {
  if (!schema.requests.update) {
    router.push({
      name: schema.crud.update as string,
      params: {
        id: item.id,
      },
    });
    return;
  }
  selectedItem.value = item;
  type.value = SchemaType.UPDATE;
  isDrawerVisible.value = true;
}

function onClickDelete(item: T) {
  confirm.require({
    message: "Are you sure you want to delete this item?",
    header: "Confirmation",
    icon: "pi pi-exclamation-triangle",
    accept: () => {
      console.log("Delete item:", item);
    },
  });
}

function onClickChildEntity(routeName: string, item: T) {
  const childEntity = schema.childEntities?.find((child) => child.route.name === routeName);

  if (childEntity) {
    router.push({
      name: childEntity.route.name,
      params: { id: item.id },
    });
  }
}

function onClickUpdate(item: T) {
  const detailRouteName = `${route.name as string}-detail`;
  if (router.hasRoute(detailRouteName) && item.id) {
    router.push({ name: detailRouteName, params: { id: item.id } });
  }
}

function onClickDetails(item: T) {
  const detailRouteName = `${route.name as string}-detail`;
  if (router.hasRoute(detailRouteName) && item.id) {
    router.push({ name: detailRouteName, params: { id: item.id } });
  }
}

function setRelationMenuRef(el: any, index: number, columnName: string) {
  relationMenuRefs.value[`${index}_${columnName}`] = el;
}

function toggleRelationMenu(event: MouseEvent, item: T, columnName: string, index: number) {
  selectedRelationEntityName.value = columnName;
  selectedItem.value = item;
  relationMenuRefs.value[`${index}_${columnName}`].toggle(event);
}

function navigateToRelation(relation: string, item: T) {
  router.push({
    name: pluralize(relation),
    query: {
      uuid: item.id,
    },
  });
}

function isRelation(value: T | string) {
  return typeof value === "object" && value !== null && "id" in value;
}

function onEditRelation(relation: string, item: T) {
  const pluralRelation = pluralize(relation);
  const detailRoute = `${pluralRelation}-detail`;
  if (router.hasRoute(detailRoute) && item.id) {
    router.push({ name: detailRoute, params: { id: item.id } });
  } else if (router.hasRoute(pluralRelation) && item.id) {
    router.push({ name: pluralRelation, query: { uuid: item.id } });
  }
}

const searchQuery = ref<string | null>(null);
const first = ref(0);
const rowsPerPage = ref(20);

const totalRecords = computed(() => {
  const response = fetchAllData.value as Record<string, any> | undefined;
  if (!response?.total_pages) return 0;
  return response.total_pages * (response.limit ?? rowsPerPage.value);
});

function fetchData(search?: string | null, page = 1, limit = rowsPerPage.value) {
  const body: Record<string, any> = {};

  if (search && schema.filterFields) {
    const isNumeric = !isNaN(Number(search));
    const excludedKeys = new Set(["match_mode", "order_by"]);
    const allowedFields = schema.searchQueryApiFields
      ? new Set(schema.searchQueryApiFields)
      : null;

    Object.entries(schema.filterFields).forEach(([field, meta]: [string, any]) => {
      if (excludedKeys.has(field)) return;
      if (allowedFields && !allowedFields.has(field)) return;
      if (meta.type === "number") {
        if (isNumeric) body[field] = Number(search);
      } else if (meta.type === "string" && !meta.options) {
        body[field] = search;
      }
    });
    body.match_mode = "or";
  }

  mutateAsync({ params: { page, limit }, data: body });
}

const debouncedFetchData = useDebounceFn((search: string | null) => {
  first.value = 0;
  fetchData(search, 1);
}, 300);

watch(searchQuery, (newValue) => {
  debouncedFetchData(newValue);
});

function onPage(event: { page: number; rows: number; first: number }) {
  first.value = event.first;
  rowsPerPage.value = event.rows;
  fetchData(searchQuery.value, event.page + 1, event.rows);
}

function handleNestedDataTableAdd() {
  fetchData();
}

function handleSubmitEntityDrawer() {
  fetchData();
}

onMounted(() => {
  if (!uuidParam.value) {
    fetchData(searchQuery.value);
  }
});

function showArrayDialog(data: any[], columnName: string, parentId: string) {
  const columnOptions = tableColumnsDefault?.find((col) => col.name === columnName)?.options;

  arrayDialogData.value = data;
  arrayDialogEntityName.value = columnName;
  arrayDialogOptions.value = columnOptions;
  arrayDialogParentId.value = parentId || '';
  if (data.length > 0) {
    if (typeof data[0] === "object") {
      arrayColumns.value = Object.keys(data[0]);
    } else {
      arrayColumns.value = ["value"];
      arrayDialogData.value = data.map((item) => ({ value: item }));
    }
  }

  isArrayDialogVisible.value = true;
}
</script>

<template>
  <!-- <SharedBreadCrumb :items="[{ label: schema.entity }]" /> -->
  <SharedDataTable
    v-model:filter-type="filterType"
    v-model:search-query="searchQuery"
    v-model:first="first"
    :unique-key="`${schema.entity}-view`"
    :data="data"
    :loading="isPending"
    :filters="filters"
    :global-filters="[]"
    :lazy="true"
    :total-records="totalRecords"
    :actions="actions"
    :child-entities="schema.childEntities"
    @click:create="onClickCreate"
    @click:edit="onClickEdit"
    @click:view="onClickUpdate"
    @click:details="onClickDetails"
    @click:child-entity="onClickChildEntity"
    @click:delete="onClickDelete"
    @page="onPage"
  >
    <template #header-left>
      <slot name="header-left" />
    </template>
    <template #header-right>
      <slot name="header-right" />
    </template>
    <template #columns>
      <slot name="columns">
        <template v-for="(column, columnName) in crudTableColumns" :key="columnName">
          <Column :field="`${columnName}`" :header="`${columnName}`" :sortable="true">
            <template #body="slotProps">
              <template v-if="Array.isArray(slotProps.data[columnName])">
                <Button
                  type="button"
                  icon="pi pi-eye"
                  class="p-button-text pl-0"
                  @click="showArrayDialog(slotProps.data[columnName], columnName.toString(), slotProps.data.id)"
                  :label="`View (${slotProps.data[columnName].length})`"
                />
              </template>
              <template v-else-if="isRelation(slotProps.data[columnName])">
                <Button
                  type="button"
                  icon="pi pi-angle-down"
                  class="p-button-text p-button-plain"
                  @click="
                    toggleRelationMenu($event, slotProps.data[columnName], columnName.toString(), slotProps.index)
                  "
                  :label="slotProps.data[columnName].name ?? 'View'"
                />
                <TieredMenu
                  :ref="(el) => setRelationMenuRef(el, slotProps.index, columnName.toString())"
                  :model="relationMenuItems"
                  :popup="true"
                />
              </template>
              <template v-else-if="shouldDisplayAsJson(slotProps.data[columnName], column)">
                <SharedJsonDisplay :value="slotProps.data[columnName]" :max-length="100" />
              </template>
              <template v-else>
                {{ slotProps.data[columnName] }}
              </template>
            </template>
          </Column>
        </template>
      </slot>
    </template>
  </SharedDataTable>

  <SharedEntityDrawer
    v-if="type"
    :key="selectedItem?.id"
    v-model:visible="isDrawerVisible"
    :schema="schema"
    :type="type"
    :data="selectedItem"
    @submit="handleSubmitEntityDrawer"
  />

  <SharedEntityNestedDataTable
    v-if="isArrayDialogVisible && arrayDialogEntityName"
    v-model:visible="isArrayDialogVisible"
    :header="arrayDialogEntityName"
    :schema="schema"
    :parent-id="arrayDialogParentId"
    :data="arrayDialogData"
    :options="arrayDialogOptions"
    @delete="(item) => emit('delete:array-item', item)"
    @add="handleNestedDataTableAdd"
  />

</template>
